import classNames from "classnames";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { languages, LanguageCode } from "../../i18n";
import "flag-icons/css/flag-icons.min.css";

const LanguageSelector: React.FC = () => {
  const { i18n, t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const normalizedLang = i18n.language.split("-")[0] as LanguageCode;
  const currentLanguage = languages[normalizedLang] ? normalizedLang : "en";

  const handleLanguageChange = (languageCode: LanguageCode) => {
    i18n.changeLanguage(languageCode);

    // dispatch custom event to notify other parts of the app
    window.dispatchEvent(new CustomEvent("languageChanged", { detail: languageCode }));

    setIsOpen(false);
  };

  return (
    <div className="language-selector">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="language-selector__toggle"
        aria-label={t("language.select")}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span
          className={`language-selector__flag fi fi-${languages[currentLanguage]?.flag}`}
          aria-hidden="true"
        ></span>
        <svg
          className={classNames("language-selector__chevron", {
            "language-selector__chevron--open": isOpen,
          })}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          <div className="language-selector__backdrop" onClick={() => setIsOpen(false)} />

          <div className="language-selector__menu" role="dialog" aria-label={t("language.select")}>
            <div className="language-selector__menu-header">{t("language.select")}</div>
            <ul
              className="language-selector__list"
              role="listbox"
              aria-label={t("language.select")}
            >
              {Object.entries(languages).map(([code, lang]) => {
                const isActive = code === currentLanguage;
                const optionClasses = classNames("language-selector__option", {
                  "language-selector__option--active": isActive,
                });

                return (
                  <li key={code} className="language-selector__item" role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      onClick={() => handleLanguageChange(code as LanguageCode)}
                      className={optionClasses}
                    >
                      <span
                        className={`language-selector__flag fi fi-${lang.flag}`}
                        aria-hidden="true"
                      ></span>
                      <span className="language-selector__language-name">{lang.name}</span>
                      {isActive && (
                        <svg
                          className="language-selector__check"
                          viewBox="0 0 20 20"
                          aria-hidden="true"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageSelector;
